import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "EdTech Team Productivity and Workflow Management | TeamGrid";
const SEO_DESCRIPTION = "Scale EdTech operations with workload forecasting, customer success tracking and cross team workflow visibility without operational chaos.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/edtech-productivity-software";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const EdTechTeams = () => {
  const navigate = useNavigate();
  // FAQ data specific to EdTech Teams
  const edTechFAQs = [
    {
      question:
        "Does TeamGrid help EdTech teams manage seasonal workload peaks?",
      answer:
        "Yes, TeamGrid uses work patterns to forecast peaks and help EdTech teams optimize capacity during busy academic cycles.",
    },
    {
      question: "Can TeamGrid support cross-functional EdTech teams?",
      answer:
        "Yes, it provides shared productivity data that helps content, product, and support teams work more efficiently.",
    },
    {
      question: "Is TeamGrid suitable for remote EdTech teams?",
      answer:
        "Yes, TeamGrid improves remote team productivity visibility across locations and hybrid work models in EdTech.",
    },
  ];

  // Productivity cards data for EdTech Teams
  const productivityCards = [
    {
      title: "Seasonal Demand Planning",
      description:
        "Academic calendars create predictable but intense workload swings.",
      points: [
        "Forecasts back-to-school and semester surges",
        "Optimizes staffing during peak periods",
        "Reallocates capacity during seasonal lulls",
        "Prevents customer support backlogs",
      ],
    },
    {
      title: "Customer Success Workload\nTracking",
      description: "Onboarding and support scale unevenly across customers.",
      points: [
        "Tracks CS workload and saturation",
        "Identifies high-support accounts early",
        "Measures proactive vs reactive support impact",
        "Improves retention through better resourcing",
      ],
    },
    {
      title: "Product vs Support Balance\nInsights",
      description:
        "Too much support slows innovation; too little hurts customers.",
      points: [
        "Shows real engineering time allocation",
        "Quantifies support burden on product teams",
        "Improves roadmap predictability",
        "Reduces reactive development cycles",
      ],
    },
    {
      title: "Content Production & Review Tracking",
      description: "Educational content bottlenecks delay launches.",
      points: [
        "Tracks content creation and review stages",
        "Identifies approval delays",
        "Improves output without sacrificing quality",
        "Optimizes reviewer capacity",
      ],
    },
  ];

  // Custom features for EdTech Teams (6 features)
  const customFeatures = [
    {
      img: "/complete_feature_image1.webp",
      title: "Seasonal Capacity Intelligence",
      desc: "Plan for academic calendar peaks.",
    },
    {
      img: "/completefeature_img2.webp",
      title: "Customer Success Workload Tracking",
      desc: "Monitor CS capacity and saturation.",
    },
    {
      img: "/completefeature_image3.webp",
      title: "Engineering Time Allocation Insights",
      desc: "Balance product vs support work.",
    },
    {
      img: "/completefeature_image4.webp",
      title: "Content Production Analytics",
      desc: "Track creation and review cycles.",
    },
    {
      img: "/completefeture_image5.webp",
      title: "Cross-Team Collaboration Metrics",
      desc: "Improve coordination across functions.",
    },
    {
      img: "/completefeature_image6.webp",
      title: "Org-Wide Productivity Intelligence",
      desc: "Company-wide visibility and insights.",
    },
  ];

  // Custom outcomes for EdTech Teams
  const customOutcomes = [
    {
      percentage: "42%",
      title: "Smarter Seasonal Planning",
      desc: "Scale without chaos. 42% better peak\nseason capacity planning.",
    },
    {
      percentage: "35%",
      title: "Efficient Customer Success",
      desc: "Support more customers without overload. 35% increase in CS efficiency.",
    },
    {
      percentage: "28%",
      title: "Faster Product Delivery",
      desc: "Protect innovation time. 28% faster feature shipping.",
    },
    {
      percentage: "25%",
      title: "Higher Customer Retention",
      desc: "Prevent churn before it starts. 25% improvement in renewals.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Does TeamGrid help EdTech teams manage seasonal workload peaks?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid analyzes work patterns to forecast workload peaks and helps EdTech teams optimize capacity during busy academic cycles." } },
            { "@type": "Question", name: "Can TeamGrid support cross-functional EdTech teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid provides shared productivity insights that help content, product, and support teams collaborate more effectively." } },
            { "@type": "Question", name: "Is TeamGrid suitable for remote EdTech teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid improves productivity visibility for remote and hybrid EdTech teams across multiple locations." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="EdTech Teams"
      heroTitle="EdTech Productivity & <span style='color: #00D1C1; font-weight: 600;'>Cross-Team Workflow Software</span>"
      heroSubtitle="Scale Learning Innovation Without Operational Chaos"
      primaryButtonText="Get a Demo for Your EdTech Team"
      customPrimaryButton={customButton}
      productivityTitle="Key Productivity Areas"
      productivitySubtitle="See how EdTech teams balance growth, support, and innovation with confidence."
      featureSuiteTitle="Complete Feature Suite"
      featureSuiteSubtitle="Everything you need in EdTech productivity software"
      customFeatures={customFeatures}
      customOutcomes={customOutcomes}
      whyChooseTitle="Why EdTech Teams Choose TeamGrid"
      whyChooseDescription="TeamGrid connects product, support, and content work into one clear view helping EdTech companies scale sustainably while delivering great learning outcomes."
      whyChooseButtonText="See TeamGrid in Action"
      productivityCards={productivityCards}
      productivityCardIcons={[
        "/EdTech_icon1.webp",
        "/Edtech_icon2.webp",
        "/Edteh_Icon3.webp",
        "/Edtech_icon4.webp",
      ]}
        faqs={edTechFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for EdTech teams."
      />
    </>
  );
};

export default EdTechTeams;
